<template>
  <header class="app-header">
    <div class="header-bar">
      <!-- Left: menu button -->
      <div class="header-left">
        <button class="header-icon-btn" @click="showMenu" aria-label="Menu">
          <i class="lni lni-menu"></i>
        </button>
      </div>

      <!-- Right: action icons -->
      <div class="header-right">
        <router-link
          class="header-icon-btn"
          :to="{ name: 'index' }"
          v-if="$route.name != 'index'"
          v-b-tooltip.hover
          title="Create Khatm"
        >
          <i class="lni lni-plus"></i>
        </router-link>

        <router-link
          class="header-icon-btn"
          :to="{ name: 'support' }"
          v-b-tooltip.hover
          title="Help & FAQ"
        >
          <i class="lni lni-question-circle"></i>
        </router-link>

        <a
          v-if="!user"
          :href="googleAuthUrl"
          class="header-icon-btn"
          v-b-tooltip.hover
          title="Continue with Google"
          aria-label="Continue with Google"
        >
          <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
        </a>

        <router-link
          v-if="!user"
          class="header-icon-btn"
          :to="{ name: 'login' }"
          v-b-tooltip.hover
          title="Login"
        >
          <i class="lni lni-user"></i>
        </router-link>

        <router-link
          v-if="user"
          class="header-user-btn"
          :to="{ name: 'dashboard' }"
          v-b-tooltip.hover
          title="My Account"
        >
          <span class="header-avatar">{{ userInitials }}</span>
        </router-link>
      </div>
    </div>

    <b-modal
      v-model="menuModal"
      hide-header
      hide-footer
      body-class="p-0"
      modal-class="menu-modal"
      size="sm"
      centered
    >
      <div class="menu-container">
        <!-- Close button -->
        <div class="text-right px-3 pt-3">
          <span class="pointer text-muted" style="font-size:22px;" @click="menuModal = false">&times;</span>
        </div>

        <!-- User greeting -->
        <div class="px-4 pb-3" v-if="user">
          <h5 class="mb-0 font-weight-bold">Assalaamu Alaikum,</h5>
          <small class="text-muted">{{ user.name }}</small>
        </div>
        <div class="px-4 pb-3" v-else>
          <h5 class="mb-0 font-weight-bold">Menu</h5>
        </div>

        <hr class="my-0">

        <!-- Main navigation -->
        <div class="menu-section">
          <a @click="goTo('index')" class="menu-item">
            <i class="lni lni-plus"></i>
            <span>Create Khatm</span>
          </a>
          <a @click="goTo('index', '#create-counter')" class="menu-item">
            <i class="lni lni-circle-plus"></i>
            <span>Create Counter</span>
          </a>
          <a @click="goTo('quranReader')" class="menu-item">
            <i class="lni lni-book"></i>
            <span>Read Quran</span>
          </a>
          <a @click="goTo('qiblaCompass')" class="menu-item">
            <svg class="qibla-menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="m16 8-2.5 5.5L8 16l2.5-5.5Z" />
            </svg>
            <span>Qibla Compass</span>
          </a>
          <a @click="goTo('tasbih')" class="menu-item">
            <i class="lni lni-leaf"></i>
            <span>Tasbih Counter</span>
          </a>
          <a v-if="user" @click="goTo('dailyDeeds')" class="menu-item">
            <i class="lni lni-calendar"></i>
            <span>Daily Deed Tracker</span>
          </a>
        </div>

        <hr class="my-0" v-if="user">

        <!-- User section -->
        <div class="menu-section" v-if="user">
          <a @click="goTo('myKhatms')" class="menu-item">
            <i class="lni lni-book"></i>
            <span>My Khatms</span>
          </a>
          <a @click="goTo('myCounters')" class="menu-item">
            <i class="lni lni-grid-alt"></i>
            <span>My Counters</span>
          </a>
          <a @click="goTo('my-account')" class="menu-item">
            <i class="lni lni-user"></i>
            <span>My Account</span>
          </a>
        </div>

        <hr class="my-0">

        <!-- Support & auth -->
        <div class="menu-section">
          <a @click="goTo('support')" class="menu-item">
            <i class="lni lni-question-circle"></i>
            <span>Help / F.A.Q</span>
          </a>
          <a v-if="!user" @click="goTo('login')" class="menu-item">
            <i class="lni lni-enter"></i>
            <span>Login</span>
          </a>
          <a v-if="!user" @click="goTo('register')" class="menu-item">
            <i class="lni lni-pencil-alt"></i>
            <span>Create Account</span>
          </a>
          <a v-if="user" @click="goTo('logoutAll')" class="menu-item text-danger">
            <i class="lni lni-exit"></i>
            <span>Sign Out</span>
          </a>
        </div>

        <div class="pb-3"></div>
      </div>
    </b-modal>
  </header>
</template>

<script>

export default {
  data() {
    return {
      menuModal: false,
    };
  },
  methods: {

    showMenu() {
      this.menuModal = true;
    },
    goTo(to, hash){
      this.menuModal = false;
      this.$router.push({ name: to, hash }).catch(() => {
        // already on this exact route — just bring the section back into view
        const el = hash && document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      });
    }
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    userInitials() {
      if (!this.user || !this.user.name) return '';
      const parts = this.user.name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
      }
      return parts[0].charAt(0).toUpperCase();
    },
    googleAuthUrl() {
      return '/auth/google';
    },
  },
  watch: {
    $route() {
      this.menuModal = false;
    },
  },
};
</script>

<style scoped>
.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}
.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}
.header-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: #444;
  font-size: 18px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
  padding: 0;
}
.header-icon-btn:hover {
  background: #f0f0f5;
  color: #1a1a2e;
  text-decoration: none;
}
.header-user-btn {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  margin-left: 2px;
}
.header-user-btn:hover {
  text-decoration: none;
}
.header-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #EFFCEF;
  color: #2d6e2d;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.15s;
  letter-spacing: -0.5px;
}
.header-user-btn:hover .header-avatar {
  box-shadow: 0 0 0 3px rgba(80, 180, 80, 0.2);
}
</style>

<style>
.menu-modal .modal-content {
  border: none;
  border-radius: 16px;
  overflow: hidden;
}
.menu-modal .modal-body {
  padding: 0;
}
.menu-container {
  background: #fff;
}
.menu-container hr {
  border-color: #eee;
}
.menu-section {
  padding: 8px 0;
}
.menu-item {
  display: flex;
  align-items: center;
  padding: 11px 24px;
  color: #333;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  font-size: 15px;
}
.menu-item:hover {
  background-color: #f5f5f5;
  text-decoration: none;
  color: #333;
}
.menu-item.text-danger:hover {
  background-color: #fff5f5;
}
.menu-item i, .qibla-menu-icon {
  width: 24px;
  font-size: 18px;
  margin-right: 14px;
  text-align: center;
  opacity: 0.7;
}
.menu-item.text-danger i {
  opacity: 1;
}
</style>
